import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Mail, ArrowLeft, Send, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [resetToken, setResetToken] = useState(null); // For testing - remove in production

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${API}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSubmitted(true);
        // For testing purposes - shows the token (remove in production)
        if (data.debug_token) {
          setResetToken(data.debug_token);
        }
        toast.success('Check your email for reset instructions');
      } else {
        toast.error(data.detail || 'Failed to send reset email');
      }
    } catch (error) {
      toast.error('Unable to connect to server');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full space-y-8">
          {/* Success State */}
          <div className="text-center">
            <div className="mx-auto w-16 h-16 border-2 border-primary bg-primary/10 rounded-full flex items-center justify-center mb-6">
              <CheckCircle className="w-8 h-8 text-primary" />
            </div>
            <h1 className="font-accent text-4xl text-foreground mb-2">CHECK YOUR EMAIL</h1>
            <p className="font-mono text-sm text-muted-foreground">
              If an account exists for <span className="text-primary">{email}</span>, you'll receive a password reset link shortly.
            </p>
          </div>

          {/* Testing Token Display - Remove in production */}
          {resetToken && (
            <div className="border-2 border-primary bg-primary/10 p-4">
              <p className="font-mono text-xs text-muted-foreground uppercase mb-2">
                Debug: Reset Token (for testing)
              </p>
              <Link 
                to={`/reset-password?token=${resetToken}`}
                className="font-mono text-sm text-primary hover:underline break-all"
              >
                Click here to reset password →
              </Link>
            </div>
          )}

          <div className="border-2 border-border bg-card p-6 space-y-4">
            <p className="font-mono text-sm text-muted-foreground">
              Didn't receive the email? Check your spam folder or try again.
            </p>
            <Button
              onClick={() => {
                setSubmitted(false);
                setResetToken(null);
              }}
              variant="outline"
              className="w-full font-mono uppercase"
            >
              Try Again
            </Button>
          </div>

          <Link 
            to="/login" 
            className="flex items-center justify-center gap-2 font-mono text-sm text-muted-foreground hover:text-primary"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <h1 className="font-accent text-4xl text-foreground mb-2">FORGOT PASSWORD</h1>
          <p className="font-mono text-sm text-muted-foreground">
            Enter your email and we'll send you a reset link
          </p>
        </div>

        {/* Form */}
        <div className="border-2 border-border bg-card p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="font-mono text-sm uppercase">
                Email Address
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="pl-10 bg-input border-2 border-border font-mono"
                  data-testid="forgot-email-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full border-2 border-primary bg-primary text-black hover:bg-transparent hover:text-primary font-mono uppercase tracking-widest btn-brutal py-6"
              data-testid="forgot-submit-btn"
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
              <Send className="w-4 h-4 ml-2" />
            </Button>
          </form>
        </div>

        {/* Back to Login */}
        <Link 
          to="/login" 
          className="flex items-center justify-center gap-2 font-mono text-sm text-muted-foreground hover:text-primary"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Login
        </Link>
      </div>
    </div>
  );
};

export default ForgotPassword;
